import { webApi } from '@/shared/api/WebApi';
import { FACTORY_ASSET_SET_PLATFORM_FIELD_ENABLED } from '@/lib/constants/appPlatform';
import type {
  FactoryReleasesRes,
  FactoryBuildsRes,
  FactorySubmissionsRes,
  FactoryTasksRes,
  FactoryActiveSubmissionsRes,
  FactoryDevicesRes,
  FactoryCampaignsRes,
  FactoryTargetsRes,
  FactoryAssetSetsRes,
  FactoryAssetSetCreateReq,
  FactoryAssetSetCreateRes,
  FactoryAssetSet,
  FactoryStoreAccount,
  FactoryStoreAccountCreateReq,
  FactoryStoreAccountTransitionReq,
  FactoryStoreAccountsRes,
  FactoryStoreAppCheck,
  FactorySubmission,
  SubmissionDownloads,
} from '@/lib/types/AppFactory';

/**
 * App factory proxy routes (app/factory/*). The api service forwards these to
 * the factory daemon; when the gateway is disabled the routes 404 — callers
 * treat any error as "factory status unavailable" and degrade quietly.
 */
export class AppFactoryApi {
  // ── Customer surface ──

  static async getReleases(cId: number): Promise<FactoryReleasesRes> {
    return webApi.get<FactoryReleasesRes>('app/factory/releases', { cId });
  }

  static async getTasks(cId: number): Promise<FactoryTasksRes> {
    return webApi.get<FactoryTasksRes>('app/factory/tasks', { cId });
  }

  static async getBuilds(cId: number, appCfgId: number): Promise<FactoryBuildsRes> {
    return webApi.get<FactoryBuildsRes>(`app/factory/${appCfgId}/builds`, { cId });
  }

  static async getSubmissions(cId: number, appCfgId: number): Promise<FactorySubmissionsRes> {
    return webApi.get<FactorySubmissionsRes>(`app/factory/${appCfgId}/submissions`, { cId });
  }

  /** Short-lived presigned HTTPS URLs for the submission's pkg + doc (Tier 1). */
  static async getSubmissionDownloads(
    cId: number,
    appCfgId: number,
    submissionId: string,
  ): Promise<SubmissionDownloads> {
    return webApi.get<SubmissionDownloads>(
      `app/factory/${appCfgId}/submissions/${submissionId}/downloads`,
      { cId },
    );
  }

  // ── Store accounts (4.2; build_spec_20260916 §3.1) ──

  /** Rows for the account and its parent chain, plus whom to invite per store. */
  static async getStoreAccounts(cId: number): Promise<FactoryStoreAccountsRes> {
    return webApi.get<FactoryStoreAccountsRes>('app/factory/store-accounts', { cId });
  }

  static async createStoreAccount(
    cId: number,
    req: FactoryStoreAccountCreateReq,
  ): Promise<FactoryStoreAccount> {
    return webApi.post<FactoryStoreAccount>('app/factory/store-accounts', { cId }, req);
  }

  /** The holder records pending→invited (and invited→active when it submits itself); the api derives the actor. */
  static async transitionStoreAccount(
    cId: number,
    appStoreAccountId: string,
    body: FactoryStoreAccountTransitionReq,
  ): Promise<FactoryStoreAccount> {
    return webApi.post<FactoryStoreAccount>(
      `app/factory/store-accounts/${appStoreAccountId}/transition`,
      { cId },
      body,
    );
  }

  /** The account covering one app on one store (own row, else a parent's covering row); 404 when none. */
  static async resolveStoreAccount(
    cId: number,
    appCfgId: number,
    store: string,
  ): Promise<FactoryStoreAccount> {
    return webApi.get<FactoryStoreAccount>(`app/factory/${appCfgId}/store-account`, {
      cId,
      store,
    });
  }

  /**
   * The farm's verdict on the store app id saved for one platform of the app
   * (ADR-0029). The read records a check request when no fresh verdict exists;
   * a daemon older than the check, a platform without one, or a factory that is
   * down all answer with an error — the caller hides the line.
   */
  static async getStoreAppCheck(
    cId: number,
    appCfgId: number,
    platform: string,
  ): Promise<FactoryStoreAppCheck> {
    return webApi.get<FactoryStoreAppCheck>(`app/factory/${appCfgId}/store-app-check`, {
      cId,
      platform,
    });
  }

  /** Tenant-scoped submission transition (B3): the customer or reseller records a portal step on its own app. */
  static async transitionOwnSubmission(
    cId: number,
    appCfgId: number,
    submissionId: string,
    body: { from: string; to: string; note?: string },
  ): Promise<FactorySubmission> {
    return webApi.post<FactorySubmission>(
      `app/factory/${appCfgId}/submissions/${submissionId}/transition`,
      { cId },
      body,
    );
  }

  // ── Brand kit (WS-2.5 asset sets) ──

  /**
   * The asset-set `platform` rides a request only once the constant is on
   * (appPlatform.ts says when): until then every call is Roku's, exactly as today.
   */
  private static assetSetPlatform(platform?: string): { platform?: string } {
    return FACTORY_ASSET_SET_PLATFORM_FIELD_ENABLED && platform ? { platform } : {};
  }

  static async getAssetSets(
    cId: number,
    appCfgId: number,
    platform?: string,
  ): Promise<FactoryAssetSetsRes> {
    return webApi.get<FactoryAssetSetsRes>(`app/factory/${appCfgId}/asset-sets`, {
      cId,
      ...AppFactoryApi.assetSetPlatform(platform),
    });
  }

  static async createAssetSet(
    cId: number,
    appCfgId: number,
    req: FactoryAssetSetCreateReq,
    platform?: string,
  ): Promise<FactoryAssetSetCreateRes> {
    return webApi.post<FactoryAssetSetCreateRes>(`app/factory/${appCfgId}/asset-set`, { cId }, {
      ...req,
      ...AppFactoryApi.assetSetPlatform(platform),
    });
  }

  static async decideAssetSet(
    cId: number,
    appCfgId: number,
    assetSetId: string,
    state: 'approved' | 'rejected',
  ): Promise<{ assetSet: FactoryAssetSet }> {
    return webApi.post<{ assetSet: FactoryAssetSet }>(
      `app/factory/${appCfgId}/asset-sets/${assetSetId}/decision`,
      { cId },
      { state },
    );
  }

  // ── Operator surface (server-side allowlist; 403 for everyone else) ──

  /** Operator ledger: every in-flight submission across apps (operator gate). */
  static getActiveSubmissions(cId: number) {
    return webApi.get<FactoryActiveSubmissionsRes>('app/factory/submissions', { cId });
  }

  static async getDevices(cId: number): Promise<FactoryDevicesRes> {
    return webApi.get<FactoryDevicesRes>('app/factory/devices', { cId });
  }

  static async getCampaigns(cId: number): Promise<FactoryCampaignsRes> {
    return webApi.get<FactoryCampaignsRes>('app/factory/campaigns', { cId });
  }

  static async getCampaignTargets(cId: number, campaignId: string): Promise<FactoryTargetsRes> {
    return webApi.get<FactoryTargetsRes>(`app/factory/campaigns/${campaignId}/targets`, { cId });
  }

  static async advanceCampaign(cId: number, campaignId: string): Promise<unknown> {
    return webApi.post<unknown>(`app/factory/campaigns/${campaignId}/advance`, { cId }, {});
  }

  static async abortCampaign(cId: number, campaignId: string): Promise<unknown> {
    return webApi.post<unknown>(`app/factory/campaigns/${campaignId}/abort`, { cId }, {});
  }

  static async transitionSubmission(
    cId: number,
    submissionId: string,
    body: { from: string; to: string; note?: string },
  ): Promise<unknown> {
    return webApi.post<unknown>(`app/factory/submissions/${submissionId}/transition`, { cId }, body);
  }
}
